import { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { ClipboardText, CaretLeft, CaretRight, X, Funnel, Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DAYS, TIME_SLOTS, ATTENDANCE_STATUS, STATUS_COLORS, LESSON_COLORS } from './constants';

export const AttendanceView = ({ lessons, students, onTotalChange, anno }) => {
  const [attendance, setAttendance] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusCounts, setStatusCounts] = useState({});

  // Filters
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStudent, setFilterStudent] = useState('');

  // Bulk create modal
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    student_id: '', date_from: '', date_to: '', day: 0, start_time: '13:00', end_time: '13:45'
  });

  // Edit modal
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({ date: '', day: 0, start_time: '', end_time: '' });

  const LIMIT = 30;

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: LIMIT };
      if (anno) params.anno = anno;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      if (filterStudent) params.student_name = filterStudent;
      const response = await api.getAttendance(params);
      const data = response.data;
      setAttendance(data.records);
      setTotal(data.total);
      setPages(data.pages);
      setStatusCounts(data.status_counts || {});
      if (onTotalChange) onTotalChange(data.total);
    } catch (error) {
      console.error('Error loading attendance:', error);
    } finally {
      setLoading(false);
    }
  }, [page, filterDateFrom, filterDateTo, filterStudent, onTotalChange, anno]);

  useEffect(() => { loadAttendance(); }, [loadAttendance]);
  useEffect(() => { setPage(1); }, [filterDateFrom, filterDateTo, filterStudent]);

  const getStudentColorIndex = (studentId) => {
    const studentIds = [...new Set(lessons.map(l => l.student_id))];
    return studentIds.indexOf(studentId) % LESSON_COLORS.length;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const handleStatusChange = async (record, newStatus) => {
    try {
      await api.createAttendance({ lesson_id: record.lesson_id, date: record.date, status: newStatus, notes: record.notes || '' });
      toast.success('Presenza aggiornata');
      loadAttendance();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleNotesChange = async (record, notes) => {
    try {
      await api.createAttendance({ lesson_id: record.lesson_id, date: record.date, status: record.status || 'Presente', notes });
      loadAttendance();
    } catch (error) {
      toast.error('Errore nel salvataggio note');
    }
  };

  const clearFilters = () => { setFilterDateFrom(''); setFilterDateTo(''); setFilterStudent(''); setPage(1); };
  const hasFilters = filterDateFrom || filterDateTo || filterStudent;
  const isStudentFiltered = !!filterStudent;
  const studentNames = [...new Set(students.map(s => s.name.toUpperCase()))].sort();

  // Bulk create
  const handleBulkCreate = async (e) => {
    e.preventDefault();
    try {
      const result = await api.bulkCreateAttendance(bulkForm);
      toast.success(`Create ${result.data.created} lezioni`);
      setShowBulkModal(false);
      setBulkForm({ student_id: '', date_from: '', date_to: '', day: 0, start_time: '13:00', end_time: '13:45' });
      loadAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    }
  };

  // Edit record
  const openEditModal = (record) => {
    setEditingRecord(record);
    setEditForm({ date: record.date, day: record.day, start_time: record.start_time, end_time: record.end_time });
    setShowEditModal(true);
  };

  const handleEditSave = async (e) => {
    e.preventDefault();
    try {
      const jsDay = new Date(editForm.date).getDay();
      const day = jsDay === 0 ? 6 : jsDay - 1;
      await api.updateAttendanceDetails(editingRecord.id, { ...editForm, day });
      toast.success('Lezione aggiornata');
      setShowEditModal(false);
      loadAttendance();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Eliminare questa riga?')) return;
    try {
      await api.deleteAttendance(editingRecord.id);
      toast.success('Riga eliminata');
      setShowEditModal(false);
      loadAttendance();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  return (
    <div>
      {/* Filters Bar */}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-[#52525B]">
          <Funnel size={16} />
          <span className="font-medium">Lezioni dal</span>
        </div>
        <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="w-auto rounded-sm h-9 text-sm" data-testid="filter-date-from" />
        <span className="text-sm text-[#52525B]">al</span>
        <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="w-auto rounded-sm h-9 text-sm" data-testid="filter-date-to" />

        <Select value={filterStudent} onValueChange={setFilterStudent}>
          <SelectTrigger className="w-[200px] rounded-sm h-9 text-sm uppercase" data-testid="filter-student">
            <SelectValue placeholder="Tutti gli alunni" />
          </SelectTrigger>
          <SelectContent>
            {studentNames.map(name => (
              <SelectItem key={name} value={name} className="uppercase">{name}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {hasFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-sm text-[#52525B]" data-testid="clear-filters-btn">
            <X size={14} className="mr-1" />
            Pulisci filtri
          </Button>
        )}

        <Button onClick={() => setShowBulkModal(true)} size="sm" className="bg-[#0A0A0A] rounded-sm h-9 ml-auto" data-testid="add-lessons-btn">
          <Plus size={14} className="mr-1" />
          Aggiungi Lezioni
        </Button>

        <div className="text-sm text-[#52525B] font-medium" data-testid="attendance-total">
          {total} {total === 1 ? 'presenza' : 'presenze'}
        </div>
      </div>

      {/* Status Summary Cards */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4" data-testid="attendance-summary-bar">
        {ATTENDANCE_STATUS.map(status => {
          const count = statusCounts[status] || 0;
          return (
            <div key={status} className={`px-2 py-2 rounded-sm border text-center ${STATUS_COLORS[status]}`} data-testid={`attendance-summary-${status}`}>
              <div className="text-lg font-bold font-['Work_Sans'] leading-tight">{count}</div>
              <div className="text-[9px] font-semibold uppercase tracking-wider leading-tight">{status}</div>
            </div>
          );
        })}
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : attendance.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <ClipboardText size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">{hasFilters ? 'Nessuna presenza trovata con i filtri selezionati' : 'Nessuna presenza registrata'}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E4E4E7] bg-[#FAFAFA]">
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Data</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Alunno</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Orario</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Rilevazione</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Annotazioni</th>
                <th className="text-center px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[#52525B]">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {attendance.map(record => {
                const colorIdx = getStudentColorIndex(record.student_id);
                return (
                  <tr key={record.id} className="border-b border-[#E4E4E7] hover:bg-[#FAFAFA]" data-testid={`attendance-row-${record.id}`}>
                    <td className="px-3 py-1.5 text-sm"><span className="capitalize">{formatDate(record.date)}</span></td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: LESSON_COLORS[colorIdx]?.border || '#ccc' }} />
                        <span className="font-medium uppercase text-sm">{record.student_name}</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-[#52525B] text-sm">{record.start_time} - {record.end_time}</td>
                    <td className="px-3 py-1.5">
                      <Select value={record.status || ''} onValueChange={(value) => handleStatusChange(record, value)}>
                        <SelectTrigger className={`w-[200px] rounded-sm text-sm ${record.status ? STATUS_COLORS[record.status] || '' : 'text-[#52525B]'}`} data-testid={`attendance-select-${record.id}`}>
                          <SelectValue placeholder="Seleziona stato" />
                        </SelectTrigger>
                        <SelectContent>
                          {ATTENDANCE_STATUS.map(status => (
                            <SelectItem key={status} value={status}>{status}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-1.5">
                      <Input
                        placeholder="Note..."
                        className="rounded-sm text-sm h-8"
                        defaultValue={record.notes || ''}
                        onBlur={(e) => { if (e.target.value !== (record.notes || '')) handleNotesChange(record, e.target.value); }}
                        data-testid={`attendance-notes-${record.id}`}
                      />
                    </td>
                    <td className="px-3 py-1.5 text-center">
                      <Button variant="ghost" size="sm" onClick={() => openEditModal(record)} className="h-7 px-2 text-[#52525B] hover:text-[#0A0A0A]" data-testid={`edit-attendance-${record.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-between mt-3 mb-12">
          <div className="text-sm text-[#52525B]">Pagina {page} di {pages}</div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} className="rounded-sm" data-testid="prev-page-btn">
              <CaretLeft size={14} className="mr-1" />Precedente
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page >= pages} className="rounded-sm" data-testid="next-page-btn">
              Successiva<CaretRight size={14} className="ml-1" />
            </Button>
          </div>
        </div>
      )}

      {/* Bulk Create Modal */}
      <Dialog open={showBulkModal} onOpenChange={setShowBulkModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Aggiungi Lezioni</DialogTitle>
            <DialogDescription>Crea lezioni ricorrenti per un alunno in un periodo</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleBulkCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Alunno</Label>
              <Select value={bulkForm.student_id} onValueChange={(v) => setBulkForm({...bulkForm, student_id: v})}>
                <SelectTrigger className="rounded-sm uppercase" data-testid="bulk-student-select"><SelectValue placeholder="Seleziona alunno" /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id} className="uppercase">{s.name.toUpperCase()}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Dal</Label>
                <Input type="date" value={bulkForm.date_from} onChange={(e) => setBulkForm({...bulkForm, date_from: e.target.value})} required className="rounded-sm" data-testid="bulk-date-from" />
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Al</Label>
                <Input type="date" value={bulkForm.date_to} onChange={(e) => setBulkForm({...bulkForm, date_to: e.target.value})} required className="rounded-sm" data-testid="bulk-date-to" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="label-overline">Giorno</Label>
              <Select value={bulkForm.day.toString()} onValueChange={(v) => setBulkForm({...bulkForm, day: parseInt(v)})}>
                <SelectTrigger className="rounded-sm" data-testid="bulk-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={i.toString()}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Ora Inizio</Label>
                <Select value={bulkForm.start_time} onValueChange={(v) => setBulkForm({...bulkForm, start_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="bulk-start-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Ora Fine</Label>
                <Select value={bulkForm.end_time} onValueChange={(v) => setBulkForm({...bulkForm, end_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="bulk-end-time"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    <SelectItem value="20:00">20:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowBulkModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#0A0A0A] rounded-sm" disabled={!bulkForm.student_id || !bulkForm.date_from || !bulkForm.date_to} data-testid="bulk-create-submit">Crea Lezioni</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Work_Sans']">Modifica Lezione</DialogTitle>
            <DialogDescription>Modifica data e orario della lezione</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSave} className="space-y-4">
            <div className="space-y-2">
              <Label className="label-overline">Data</Label>
              <Input type="date" value={editForm.date} onChange={(e) => setEditForm({...editForm, date: e.target.value})} required className="rounded-sm" data-testid="edit-date" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="label-overline">Ora Inizio</Label>
                <Select value={editForm.start_time} onValueChange={(v) => setEditForm({...editForm, start_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-start-time"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="label-overline">Ora Fine</Label>
                <Select value={editForm.end_time} onValueChange={(v) => setEditForm({...editForm, end_time: v})}>
                  <SelectTrigger className="rounded-sm" data-testid="edit-end-time"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    <SelectItem value="20:00">20:00</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex gap-2">
              <Button type="button" variant="destructive" onClick={handleDelete} className="mr-auto rounded-sm" data-testid="delete-attendance-btn">
                <Trash size={14} className="mr-1" />Elimina
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowEditModal(false)} className="rounded-sm">Annulla</Button>
              <Button type="submit" className="bg-[#0A0A0A] rounded-sm" data-testid="edit-save-btn">Salva</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
